/**
 * Survey Builder component for creating AI-powered surveys
 * Extends existing question parsing infrastructure
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, FileText, Users } from 'lucide-react';
import Button from '../../components/Button';
import Layout from '../../components/Layout';
import SurveyQuestionsPreview from './SurveyQuestionsPreview';
import useQuestionParsingSocket from '../../hooks/useQuestionParsingSocket';
import { createSurveyWithQuestions, createSurveyGroup, type SurveyBlockDefinition } from '../../utils/surveyApi';
import { UI_TEXT } from '../../config/ui-text';

interface ParsedQuestion {
  id: string;
  text: string;
  type: string;
  choices?: string[];
  scale_min?: number;
  scale_max?: number;
  max_selections?: number;
  ranking_count?: number;
  text_limit?: number;
  required?: boolean;
  followup_instructions?: string;
  allow_other?: boolean;
  enable_ai_followup?: boolean;
}

// Validation errors interface
interface ValidationErrors {
  surveyName?: string;
  questions?: string;
}

const SurveyBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'input' | 'preview' | 'creating'>('input');
  const [surveyName, setSurveyName] = useState<string>('');
  const [surveyTitle, setSurveyTitle] = useState<string>('');
  const [surveyDescription, setSurveyDescription] = useState<string>('');
  const [surveyInstructions, setSurveyInstructions] = useState<string>('');
  const [questions, setQuestions] = useState<string>('');
  const [goal, setGoal] = useState<string>('');
  const [context, setContext] = useState<string>('');
  const [parsedQuestions, setParsedQuestions] = useState<ParsedQuestion[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [groupCount, setGroupCount] = useState<number>(1);

  const {
    parsingState,
    triggerParsing,
    clearParsingState,
    connect
  } = useQuestionParsingSocket({
    onParsingCompleted: (data) => {
      if (data.parsed_questions && data.parsed_questions.length > 0) {
        setParsedQuestions(data.parsed_questions);
        setStep('preview');
      }
    },
    autoConnect: true
  });

  // Validate form fields
  const validateForm = useCallback((): ValidationErrors => {
    const errors: ValidationErrors = {};
    
    if (!surveyName.trim()) {
      errors.surveyName = 'Survey name is required';
    }
    
    if (!questions.trim()) {
      errors.questions = 'Please enter at least one question';
    } else {
      // Check if there are valid questions after splitting
      const questionsList = questions
        .split('\n')
        .map(q => q.trim())
        .filter(q => q.length > 0);
      
      if (questionsList.length === 0) {
        errors.questions = 'Please enter at least one valid question';
      }
    }
    
    return errors;
  }, [surveyName, questions]);

  const handleQuestionsSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    
    // Validate form
    const errors = validateForm();
    setValidationErrors(errors);
    
    // If there are validation errors, don't proceed
    if (Object.keys(errors).length > 0) {
      return;
    }

    // Split questions by line breaks and filter empty lines
    const questionsList = questions
      .split('\n')
      .map(q => q.trim())
      .filter(q => q.length > 0);

    try {
      await triggerParsing(questionsList, goal, context, undefined, true);
    } catch (error) {
      console.error('Failed to trigger question parsing:', error);
    }
  }, [questions, goal, context, triggerParsing, validateForm]);

  const handleQuestionsUpdated = useCallback((updatedQuestions: ParsedQuestion[]) => {
    setParsedQuestions(updatedQuestions);
  }, []);

  const handleCreateSurvey = useCallback(async (
    finalSurveyName: string,
    finalQuestions: ParsedQuestion[],
    redirectConfig?: any,
    maxResponses?: number | null,
    blocks?: SurveyBlockDefinition[] | null
  ) => {
    setIsCreating(true);

    try {
      if (groupCount > 1) {
        await createSurveyGroup({
          name: finalSurveyName,
          group_count: groupCount,
          questions: finalQuestions,
          blocks: blocks ?? undefined,
          title: surveyTitle.trim() || undefined,
          description: surveyDescription.trim() || undefined,
          instructions: surveyInstructions.trim() || undefined,
          redirect_config: redirectConfig,
          max_responses_per_group: maxResponses,
        });
      } else {
        await createSurveyWithQuestions(
          finalSurveyName,
          finalQuestions,
          surveyTitle.trim() || undefined,
          surveyDescription.trim() || undefined,
          surveyInstructions.trim() || undefined,
          redirectConfig,
          maxResponses,
          blocks ?? null
        );
      }

      navigate('/surveys');

    } catch (error) {
      console.error('Failed to create survey:', error);
      setIsCreating(false);
    }
  }, [navigate, surveyTitle, surveyDescription, surveyInstructions, groupCount]);

  const handleGoBack = useCallback(() => {
    if (step === 'preview') {
      setStep('input');
      setParsedQuestions([]);
      clearParsingState();
    } else {
      navigate('/surveys');
    }
  }, [step, navigate, clearParsingState]);

  if (step === 'preview') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <SurveyQuestionsPreview
            parsedQuestions={parsedQuestions}
            surveyName={surveyName}
            onQuestionsUpdated={handleQuestionsUpdated}
            onCreateSurvey={handleCreateSurvey}
            onGoBack={handleGoBack}
            isLoading={isCreating}
            groupCount={groupCount}
          />
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-sm p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleGoBack}
              icon={<ArrowLeft className="w-4 h-4 mr-1" />}
            >
              {UI_TEXT.AI_SURVEY.TITLES.BACK_TO_SURVEYS}
            </Button>
          </div>

          <div className="mb-6">
            <div className="flex items-center mb-2">
              <FileText className="w-5 h-5 text-blue-500 mr-2" />
              <h1 className="text-2xl font-bold text-gray-900">
                {UI_TEXT.AI_SURVEY.TITLES.CREATE_SURVEY}
              </h1>
            </div>
            <p className="text-gray-600">
              {UI_TEXT.AI_SURVEY.DESCRIPTIONS.MAIN}
            </p>
          </div>

          <form onSubmit={handleQuestionsSubmit} className="space-y-6">
            {/* Survey Name Input */}
            <div>
              <label htmlFor="surveyName" className="block text-sm font-medium text-gray-700 mb-2">
                {UI_TEXT.AI_SURVEY.LABELS.SURVEY_NAME} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="surveyName"
                value={surveyName}
                onChange={(e) => {
                  setSurveyName(e.target.value);
                  // Clear error when user starts typing
                  if (validationErrors.surveyName && e.target.value.trim()) {
                    setValidationErrors(prev => ({ ...prev, surveyName: undefined }));
                  }
                }}
                placeholder={UI_TEXT.AI_SURVEY.PLACEHOLDERS.SURVEY_NAME}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                  hasAttemptedSubmit && validationErrors.surveyName
                    ? 'border-red-500 focus:ring-red-500 bg-red-50'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
              />
              {hasAttemptedSubmit && validationErrors.surveyName ? (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {validationErrors.surveyName}
                </p>
              ) : (
                <p className="mt-1 text-sm text-gray-500">
                  Internal identifier for organizing your surveys
                </p>
              )}
            </div>

            {/* Survey Title Input */}
            <div>
              <label htmlFor="surveyTitle" className="block text-sm font-medium text-gray-700 mb-2">
                Survey Title (Optional)
              </label>
              <input
                type="text"
                id="surveyTitle"
                value={surveyTitle}
                onChange={(e) => setSurveyTitle(e.target.value)}
                placeholder="Title shown to survey participants"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-sm text-gray-500">
                Customer-facing title displayed on the welcome screen
              </p>
            </div>

            {/* Survey Description Input */}
            <div>
              <label htmlFor="surveyDescription" className="block text-sm font-medium text-gray-700 mb-2">
                Survey Description (Optional)
              </label>
              <textarea
                id="surveyDescription"
                value={surveyDescription}
                onChange={(e) => setSurveyDescription(e.target.value)}
                placeholder="Brief description of what this survey is about"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
              />
              <p className="mt-1 text-sm text-gray-500">
                Explain the purpose and context of the survey to participants
              </p>
            </div>

            {/* Survey Instructions Input */}
            <div>
              <label htmlFor="surveyInstructions" className="block text-sm font-medium text-gray-700 mb-2">
                Survey Instructions (Optional)
              </label>
              <textarea
                id="surveyInstructions"
                value={surveyInstructions}
                onChange={(e) => setSurveyInstructions(e.target.value)}
                placeholder="Instructions for participants on how to complete the survey"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
              />
              <p className="mt-1 text-sm text-gray-500">
                Provide guidance on how to answer questions and what to expect
              </p>
            </div>

            {/* Between-Subject Groups */}
            <div>
              <label htmlFor="groupCount" className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                Number of Groups (Optional)
              </label>
              <input
                type="number"
                id="groupCount"
                value={groupCount}
                min={1}
                max={26}
                onChange={(e) => setGroupCount(Math.max(1, Math.min(26, parseInt(e.target.value) || 1)))}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-sm text-gray-500">
                {groupCount > 1
                  ? `${groupCount} groups (A–${String.fromCharCode(64 + groupCount)}) will be created. Participants are evenly distributed across groups.`
                  : 'Set to 2 or more to enable between-subject group design.'}
              </p>
            </div>

            {/* Questions Input */}
            <div>
              <label htmlFor="questions" className="block text-sm font-medium text-gray-700 mb-2">
                {UI_TEXT.AI_SURVEY.LABELS.QUESTIONS.replace(' *', '')} <span className="text-red-500">*</span>
              </label>
              <textarea
                id="questions"
                value={questions}
                onChange={(e) => {
                  setQuestions(e.target.value);
                  // Clear error when user starts typing
                  if (validationErrors.questions && e.target.value.trim()) {
                    setValidationErrors(prev => ({ ...prev, questions: undefined }));
                  }
                }}
                placeholder={UI_TEXT.AI_SURVEY.PLACEHOLDERS.QUESTIONS}
                className={`w-full px-3 py-3 border rounded-lg resize-none focus:ring-2 focus:border-transparent transition-colors ${
                  hasAttemptedSubmit && validationErrors.questions
                    ? 'border-red-500 focus:ring-red-500 bg-red-50'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                rows={12}
              />
              {hasAttemptedSubmit && validationErrors.questions ? (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {validationErrors.questions}
                </p>
              ) : (
                <p className="mt-1 text-sm text-gray-500">
                  {UI_TEXT.AI_SURVEY.DESCRIPTIONS.QUESTIONS_HELP}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <div 
                className="relative group"
                title={(!surveyName.trim() || !questions.trim()) ? "Fill all required fields (*) to continue" : ""}
              >
                <Button
                  type="submit"
                  variant="primary"
                  disabled={parsingState.isParsing}
                  icon={<Plus className="w-4 h-4" />}
                >
                  {parsingState.isParsing ? UI_TEXT.AI_SURVEY.BUTTONS.PROCESSING : UI_TEXT.AI_SURVEY.BUTTONS.PARSE_QUESTIONS}
                </Button>
                {/* Tooltip for disabled state hint */}
                {(!surveyName.trim() || !questions.trim()) && !parsingState.isParsing && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    Fill all required fields (*) to continue
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-800"></div>
                  </div>
                )}
              </div>
            </div>
          </form>

          {/* Parsing Progress */}
          {parsingState.isParsing && (
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">
                  {UI_TEXT.AI_SURVEY.PROGRESS.PROCESSING_TITLE}
                </span>
                <span className="text-sm text-blue-700">
                  {parsingState.progress}%
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${parsingState.progress}%` }}
                ></div>
              </div>
              {parsingState.currentStep && (
                <p className="mt-2 text-sm text-blue-700">
                  {parsingState.currentStep}
                </p>
              )}
            </div>
          )}

          {/* Error Display */}
          {parsingState.error && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-red-900 mb-1">
                {UI_TEXT.AI_SURVEY.PROGRESS.PARSING_FAILED}
              </h4>
              <p className="text-sm text-red-700">
                {parsingState.error}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearParsingState}
                className="mt-2 text-red-700 hover:text-red-800"
              >
                {UI_TEXT.AI_SURVEY.BUTTONS.TRY_AGAIN}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
    </Layout>
  );
};

export default SurveyBuilder;